import { useState, useEffect, useCallback, useRef } from "react";
import {
  Calendar, Sparkles, RefreshCw, Edit3, Trash2, Check, Image,
  ChevronLeft, ChevronRight, Loader2, Copy, Upload, Link2,
  ExternalLink, Clock, TrendingUp, Eye, Heart, MessageCircle,
  Instagram, Facebook, Plus, CheckCircle2, Video, FileText, Play
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { format, startOfWeek, addWeeks, addDays, subWeeks } from "date-fns";
import { de } from "date-fns/locale";
import { useSearchParams } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr"];
const WEEKDAYS_FULL = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag"];

const DAY_COLORS = [
  { bg: "bg-blue-50", border: "border-blue-200", accent: "bg-blue-500", text: "text-blue-700" },
  { bg: "bg-emerald-50", border: "border-emerald-200", accent: "bg-emerald-500", text: "text-emerald-700" },
  { bg: "bg-amber-50", border: "border-amber-200", accent: "bg-amber-500", text: "text-amber-700" },
  { bg: "bg-purple-50", border: "border-purple-200", accent: "bg-purple-500", text: "text-purple-700" },
  { bg: "bg-rose-50", border: "border-rose-200", accent: "bg-rose-500", text: "text-rose-700" },
];

const STATUS_CONFIG = {
  draft: { label: "Entwurf", color: "bg-slate-100 text-slate-600", icon: Edit3 },
  approved: { label: "Genehmigt", color: "bg-blue-100 text-blue-700", icon: CheckCircle2 },
  published: { label: "Veröffentlicht", color: "bg-green-100 text-green-700", icon: Check }
};

export default function SocialMediaPage() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [currentWeek, setCurrentWeek] = useState(() => {
    return startOfWeek(new Date(), { weekStartsOn: 1 });
  });
  const [weekPlan, setWeekPlan] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [themeInput, setThemeInput] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [createMode, setCreateMode] = useState("theme");
  const [selectedPost, setSelectedPost] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [regeneratingContent, setRegeneratingContent] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [canvaStatus, setCanvaStatus] = useState({ configured: false, connected: false });
  const [creatingCanvaDesign, setCreatingCanvaDesign] = useState(false);
  const fileInputRef = useRef(null);

  const weekStartStr = format(currentWeek, "yyyy-MM-dd");

  useEffect(() => {
    if (searchParams.get("canva_connected") === "true") {
      toast.success("Canva erfolgreich verbunden!");
      loadCanvaStatus();
    }
    if (searchParams.get("canva_error")) {
      toast.error("Fehler bei der Canva-Verbindung");
    }
  }, [searchParams]);

  const loadCanvaStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/social/canva/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCanvaStatus(response.data);
    } catch (error) {
      console.error("Error loading Canva status:", error);
    }
  };

  useEffect(() => { loadCanvaStatus(); }, []);

  const loadWeekData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/api/social/week-plans/${weekStartStr}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWeekPlan(response.data.plan);
      setPosts(response.data.posts);
    } catch (error) {
      if (error.response?.status === 404) {
        setWeekPlan(null);
        setPosts([]);
      } else {
        toast.error("Fehler beim Laden der Daten");
      }
    } finally {
      setLoading(false);
    }
  }, [weekStartStr]);

  useEffect(() => { loadWeekData(); }, [loadWeekData]);

  useEffect(() => {
    let interval;
    if (weekPlan?.status === "generating") {
      interval = setInterval(loadWeekData, 5000);
    }
    return () => clearInterval(interval);
  }, [weekPlan?.status, loadWeekData]);

  const handleCreateWeekPlan = async () => {
    if (createMode === "theme" && !themeInput.trim()) {
      toast.error("Bitte geben Sie ein Thema ein");
      return;
    }
    if (createMode === "website" && !websiteUrl.trim()) {
      toast.error("Bitte geben Sie eine Website-URL ein");
      return;
    }

    try {
      setGenerating(true);
      setIsCreateModalOpen(false);
      const token = localStorage.getItem("token");

      if (createMode === "website") {
        await axios.post(
          `${API}/api/social/week-plans/from-website`,
          { week_start_date: weekStartStr, website_url: websiteUrl.trim() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("KI analysiert Website und generiert 5 Posts (4 Posts + 1 Reel)...");
        setWebsiteUrl("");
      } else {
        await axios.post(
          `${API}/api/social/week-plans`,
          { week_start_date: weekStartStr, main_theme: themeInput.trim() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("KI generiert 5 Posts (4 Posts + 1 Reel)...");
        setThemeInput("");
      }

      loadWeekData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen");
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteWeekPlan = async () => {
    if (!window.confirm("Wochenplan und alle Posts löschen?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/api/social/week-plans/${weekStartStr}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Gelöscht");
      setWeekPlan(null);
      setPosts([]);
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const openEditModal = (post) => {
    setSelectedPost(post);
    setEditForm({
      title: post.title,
      caption: post.caption,
      description: post.description,
      hashtags: post.hashtags?.join(", ") || "",
      status: post.status || "draft"
    });
    setIsEditModalOpen(true);
  };

  const handleSavePost = async () => {
    try {
      const token = localStorage.getItem("token");
      const payload = {
        title: editForm.title,
        caption: editForm.caption,
        description: editForm.description,
        hashtags: editForm.hashtags.split(",").map(h => h.trim()).filter(h => h),
        status: editForm.status
      };
      await axios.put(
        `${API}/api/social/posts/${selectedPost.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Gespeichert");
      setIsEditModalOpen(false);
      loadWeekData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleRegenerateContent = async () => {
    try {
      setRegeneratingContent(true);
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/api/social/posts/${selectedPost.id}/regenerate-content`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedPost(response.data);
      setEditForm({
        title: response.data.title,
        caption: response.data.caption,
        description: response.data.description,
        hashtags: response.data.hashtags?.join(", ") || "",
        status: response.data.status || "draft"
      });
      toast.success("Neuer Inhalt generiert!");
      loadWeekData();
    } catch (error) {
      toast.error("Fehler bei der Content-Generierung");
    } finally {
      setRegeneratingContent(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Ungültiger Dateityp");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Datei zu groß (max 5MB)");
      return;
    }
    try {
      setUploadingImage(true);
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(
        `${API}/api/social/posts/${selectedPost.id}/upload-image`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      setSelectedPost(response.data);
      toast.success("Bild hochgeladen!");
      loadWeekData();
    } catch (error) {
      toast.error("Fehler beim Hochladen");
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleConnectCanva = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/social/canva/auth-url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Canva-Verbindung fehlgeschlagen");
    }
  };

  const handleCreateCanvaDesign = async () => {
    try {
      setCreatingCanvaDesign(true);
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/api/social/posts/${selectedPost.id}/create-canva-design`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedPost(response.data.post);
      if (response.data.canva_edit_url) {
        window.open(response.data.canva_edit_url, "_blank");
        toast.success("Canva-Design erstellt!");
      }
      loadWeekData();
    } catch (error) {
      toast.error("Fehler beim Erstellen");
    } finally {
      setCreatingCanvaDesign(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Kopiert!");
  };

  const getPostForDay = (dayIndex) => posts.find(p => p.day_of_week === dayIndex);

  const getCanvaFullPrompt = (post) => {
    if (!post?.canva_prompt) return "";
    if (typeof post.canva_prompt === "string") return post.canva_prompt;
    return post.canva_prompt.full_prompt || JSON.stringify(post.canva_prompt, null, 2);
  };

  const weekStats = {
    total: posts.length,
    drafts: posts.filter(p => p.status === "draft").length,
    approved: posts.filter(p => p.status === "approved").length,
    published: posts.filter(p => p.status === "published").length,
    reels: posts.filter(p => p.content_type === "reels").length,
    imagePosts: posts.filter(p => p.content_type !== "reels").length
  };

  return (
    <Layout>
      <div className="space-y-4 pb-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="social-media-title">{t("social_title")}</h1>
            <p className="text-sm text-slate-500">4 Posts + 1 Reel pro Woche</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))} data-testid="prev-week-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="px-3 py-1.5 bg-slate-100 rounded-lg text-sm font-medium" data-testid="week-display">
              {format(currentWeek, "dd. MMM", { locale: de })} - {format(addDays(currentWeek, 4), "dd. MMM", { locale: de })}
            </div>
            <Button variant="ghost" size="icon" onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))} data-testid="next-week-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Quick Stats Bar */}
        <div className="flex items-center justify-between bg-white border rounded-lg p-3">
          <div className="flex items-center gap-4">
            {weekPlan ? (
              <>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-500" />
                  <span className="text-sm font-medium truncate max-w-[200px]">{weekPlan.main_theme}</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <Badge variant="outline" className="font-normal">{weekStats.total}/5 Posts</Badge>
                  <Badge variant="outline" className="font-normal bg-blue-50">
                    <Image className="w-3 h-3 mr-1" />{weekStats.imagePosts} Posts
                  </Badge>
                  <Badge variant="outline" className="font-normal bg-rose-50">
                    <Video className="w-3 h-3 mr-1" />{weekStats.reels} Reel
                  </Badge>
                </div>
                {weekPlan.status === "generating" && (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                    <span className="text-xs text-purple-600">{posts.length}/5 generiert...</span>
                  </div>
                )}
              </>
            ) : (
              <span className="text-sm text-slate-500">Kein Wochenplan</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {canvaStatus.configured && canvaStatus.connected && (
              <Badge variant="outline" className="bg-green-50 text-green-700 text-xs">
                <Link2 className="w-3 h-3 mr-1" />Canva
              </Badge>
            )}
            {weekPlan ? (
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={() => setIsCreateModalOpen(true)} data-testid="regenerate-plan-btn">
                  <RefreshCw className="w-3 h-3 mr-1" />Neu
                </Button>
                <Button variant="ghost" size="sm" onClick={handleDeleteWeekPlan} className="text-red-500" data-testid="delete-plan-btn">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ) : (
              <Button size="sm" onClick={() => setIsCreateModalOpen(true)} data-testid="create-week-plan-button">
                <Plus className="w-4 h-4 mr-1" />Wochenplan erstellen
              </Button>
            )}
          </div>
        </div>

        {/* Weekly Grid - 5 Days */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
          </div>
        ) : weekPlan?.status === "generating" ? (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500 mx-auto mb-3" />
            <p className="font-medium text-purple-800">KI generiert 4 Posts + 1 Reel...</p>
            <div className="flex justify-center gap-1 mt-3">
              {[0, 1, 2, 3, 4].map(i => (
                <div key={i} className={`w-3 h-3 rounded-full transition-all ${posts.find(p => p.day_of_week === i) ? "bg-purple-500" : "bg-purple-200"}`} />
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-5 gap-3" data-testid="weekly-grid">
            {WEEKDAYS.map((dayName, index) => {
              const post = getPostForDay(index);
              const dayDate = addDays(currentWeek, index);
              const colors = DAY_COLORS[index];
              const isReel = post?.content_type === "reels";

              return (
                <div
                  key={index}
                  className={`rounded-lg border-2 overflow-hidden transition-all hover:shadow-md cursor-pointer ${colors.bg} ${colors.border}`}
                  onClick={() => post && openEditModal(post)}
                  data-testid={`day-card-${index}`}
                >
                  {/* Day Header */}
                  <div className={`${colors.accent} text-white px-2 py-1.5 flex items-center justify-between`}>
                    <div className="flex items-center gap-1.5">
                      <span className="font-bold text-xs">{dayName}</span>
                      {isReel && <Video className="w-3 h-3" />}
                    </div>
                    <span className="text-xs opacity-90">{format(dayDate, "dd.MM")}</span>
                  </div>

                  {/* Content */}
                  <div className="p-2">
                    {post ? (
                      <div className="space-y-1.5">
                        {/* Type Badge */}
                        <div className="flex items-center justify-between">
                          <Badge
                            variant="secondary"
                            className={`text-[10px] px-1.5 py-0 ${isReel ? "bg-rose-100 text-rose-700" : "bg-blue-100 text-blue-700"}`}
                            data-testid={`post-type-badge-${index}`}
                          >
                            {isReel ? "Reel" : "Post"}
                          </Badge>
                          <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 ${STATUS_CONFIG[post.status || 'draft'].color}`}>
                            {STATUS_CONFIG[post.status || 'draft'].label}
                          </Badge>
                        </div>

                        {/* Image or Canva Prompt Preview */}
                        {post.image_url ? (
                          <div className="aspect-square rounded overflow-hidden bg-white">
                            <img src={post.image_url} alt="" className="w-full h-full object-cover" />
                          </div>
                        ) : isReel ? (
                          <div className="aspect-square rounded bg-gradient-to-br from-rose-100 to-purple-100 flex flex-col items-center justify-center gap-1">
                            <Play className="w-6 h-6 text-rose-400" />
                            <span className="text-[10px] text-rose-500 font-medium">UGC Script</span>
                          </div>
                        ) : (
                          <div className="aspect-square rounded bg-gradient-to-br from-blue-50 to-slate-100 flex flex-col items-center justify-center gap-1 p-1">
                            <FileText className="w-5 h-5 text-blue-300" />
                            <span className="text-[9px] text-slate-500 text-center line-clamp-2 leading-tight">
                              {post.canva_prompt?.design_style || post.canva_prompt?.overview || "Canva Prompt"}
                            </span>
                          </div>
                        )}

                        {/* Title */}
                        <p className="text-xs font-medium line-clamp-2 leading-tight">{post.title}</p>

                        {/* Copy Button */}
                        <div className="flex justify-end">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-5 w-5"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const fullPrompt = getCanvaFullPrompt(post);
                                    copyToClipboard(fullPrompt || `${post.caption}\n\n${post.hashtags?.map(h => `#${h}`).join(" ")}`);
                                  }}
                                  data-testid={`copy-btn-${index}`}
                                >
                                  <Copy className="w-3 h-3" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent><p>Canva-Prompt kopieren</p></TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="aspect-square rounded bg-white/30 flex flex-col items-center justify-center cursor-pointer hover:bg-white/50 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!weekPlan) setIsCreateModalOpen(true);
                        }}
                      >
                        <Plus className="w-5 h-5 text-slate-400 mb-1" />
                        <span className="text-[10px] text-slate-500">Leer</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Best Times Info */}
        {posts.length > 0 && (
          <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-100">
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium">Beste Zeiten:</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <Instagram className="w-4 h-4 text-pink-500" />
                      <span className="text-xs text-slate-600">09:00, 12:00, 19:00</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Facebook className="w-4 h-4 text-blue-600" />
                      <span className="text-xs text-slate-600">09:00, 13:00, 16:00</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <TrendingUp className="w-3 h-3" />
                  <span>Höchste Engagement-Rate</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Create Week Plan Modal */}
        <Dialog open={isCreateModalOpen} onOpenChange={(open) => {
          setIsCreateModalOpen(open);
          if (!open) { setCreateMode("theme"); setThemeInput(""); setWebsiteUrl(""); }
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Wochenplan erstellen
              </DialogTitle>
              <DialogDescription>
                Die KI erstellt 4 Posts (Mo-Do) + 1 Reel mit UGC Script (Fr).
              </DialogDescription>
            </DialogHeader>

            <Tabs value={createMode} onValueChange={setCreateMode} className="mt-2">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="theme" className="text-sm">
                  <Sparkles className="w-4 h-4 mr-2" />Thema eingeben
                </TabsTrigger>
                <TabsTrigger value="website" className="text-sm">
                  <Link2 className="w-4 h-4 mr-2" />Website analysieren
                </TabsTrigger>
              </TabsList>

              <TabsContent value="theme" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Hauptthema</Label>
                  <Input
                    value={themeInput}
                    onChange={(e) => setThemeInput(e.target.value)}
                    placeholder="z.B. EMS Training Vorteile"
                    data-testid="theme-input"
                    onKeyDown={(e) => e.key === "Enter" && handleCreateWeekPlan()}
                  />
                  <div className="flex flex-wrap gap-1 mt-2">
                    {["Fitness Motivation", "Ernährung", "EMS Erfolge", "Gesundheit"].map(tag => (
                      <Badge
                        key={tag}
                        variant="outline"
                        className="cursor-pointer hover:bg-slate-100 text-xs"
                        onClick={() => setThemeInput(tag)}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="website" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Website-URL</Label>
                  <Input
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    placeholder="https://www.ihre-website.de"
                    data-testid="website-url-input"
                    onKeyDown={(e) => e.key === "Enter" && handleCreateWeekPlan()}
                  />
                  <p className="text-xs text-slate-500">
                    Die KI analysiert Ihre Website und erstellt passende Posts.
                  </p>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-3 text-sm">
                  <p className="font-medium text-slate-700 mb-2">Was wird analysiert:</p>
                  <ul className="text-xs text-slate-600 space-y-1">
                    {["Unternehmensname & Branche", "Dienstleistungen & Produkte", "Markenwerte & Tonalität"].map(item => (
                      <li key={item} className="flex items-center gap-2">
                        <CheckCircle2 className="w-3 h-3 text-green-500" />{item}
                      </li>
                    ))}
                  </ul>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>Abbrechen</Button>
              <Button
                onClick={handleCreateWeekPlan}
                disabled={generating || (createMode === "theme" ? !themeInput.trim() : !websiteUrl.trim())}
                data-testid="generate-plan-btn"
              >
                {generating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generiert...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" />Generieren</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Post Modal */}
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" data-testid="edit-modal-title">
                {selectedPost && (
                  <>
                    {selectedPost.content_type === "reels" ? (
                      <Badge className="bg-rose-100 text-rose-700 text-xs"><Video className="w-3 h-3 mr-1" />Reel</Badge>
                    ) : (
                      <Badge className="bg-blue-100 text-blue-700 text-xs"><Image className="w-3 h-3 mr-1" />Post</Badge>
                    )}
                    {WEEKDAYS_FULL[selectedPost.day_of_week]} - {format(new Date(selectedPost.date), "dd. MMMM", { locale: de })}
                  </>
                )}
              </DialogTitle>
            </DialogHeader>

            {selectedPost && (
              <div className="grid grid-cols-2 gap-6">
                {/* Left: Canva Prompt & UGC Script */}
                <div className="space-y-4">
                  {/* Canva Prompt Section */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-semibold flex items-center gap-1">
                        <FileText className="w-4 h-4 text-blue-500" />
                        Canva Bild-Prompt
                      </Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(getCanvaFullPrompt(selectedPost))}
                        data-testid="copy-canva-prompt-btn"
                      >
                        <Copy className="w-3 h-3 mr-1" />Kopieren
                      </Button>
                    </div>
                    <div className="bg-slate-50 border rounded-lg p-3 text-sm whitespace-pre-wrap max-h-40 overflow-y-auto" data-testid="canva-prompt-display">
                      {getCanvaFullPrompt(selectedPost) || "Kein Canva-Prompt vorhanden"}
                    </div>

                    {/* Canva Prompt Details */}
                    {selectedPost.canva_prompt && typeof selectedPost.canva_prompt === "object" && (
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        {selectedPost.canva_prompt.design_style && (
                          <div className="bg-white border rounded p-2">
                            <span className="text-slate-500">Stil:</span>
                            <p className="font-medium">{selectedPost.canva_prompt.design_style}</p>
                          </div>
                        )}
                        {selectedPost.canva_prompt.message && (
                          <div className="bg-white border rounded p-2">
                            <span className="text-slate-500">Botschaft:</span>
                            <p className="font-medium">{selectedPost.canva_prompt.message}</p>
                          </div>
                        )}
                        {selectedPost.canva_prompt.mood && (
                          <div className="bg-white border rounded p-2">
                            <span className="text-slate-500">Stimmung:</span>
                            <p className="font-medium">{selectedPost.canva_prompt.mood}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* UGC Script Section (only for Reels) */}
                  {selectedPost.content_type === "reels" && selectedPost.ugc_script && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-semibold flex items-center gap-1">
                          <Video className="w-4 h-4 text-rose-500" />
                          AI UGC Ad Script
                        </Label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(selectedPost.ugc_script.full_script || JSON.stringify(selectedPost.ugc_script, null, 2))}
                          data-testid="copy-ugc-script-btn"
                        >
                          <Copy className="w-3 h-3 mr-1" />Script kopieren
                        </Button>
                      </div>
                      <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 space-y-2 text-sm max-h-60 overflow-y-auto" data-testid="ugc-script-display">
                        {selectedPost.ugc_script.hook && (
                          <div><span className="font-semibold text-rose-700">Hook:</span> {selectedPost.ugc_script.hook}</div>
                        )}
                        {selectedPost.ugc_script.problem && (
                          <div><span className="font-semibold text-rose-700">Problem:</span> {selectedPost.ugc_script.problem}</div>
                        )}
                        {selectedPost.ugc_script.solution && (
                          <div><span className="font-semibold text-rose-700">Lösung:</span> {selectedPost.ugc_script.solution}</div>
                        )}
                        {selectedPost.ugc_script.benefits && (
                          <div>
                            <span className="font-semibold text-rose-700">Vorteile:</span>
                            <ul className="list-disc list-inside ml-2">
                              {selectedPost.ugc_script.benefits.map((b, i) => <li key={i}>{b}</li>)}
                            </ul>
                          </div>
                        )}
                        {selectedPost.ugc_script.social_proof && (
                          <div><span className="font-semibold text-rose-700">Social Proof:</span> {selectedPost.ugc_script.social_proof}</div>
                        )}
                        {selectedPost.ugc_script.cta && (
                          <div><span className="font-semibold text-rose-700">CTA:</span> {selectedPost.ugc_script.cta}</div>
                        )}
                        {selectedPost.ugc_script.full_script && (
                          <div className="mt-2 pt-2 border-t border-rose-200">
                            <span className="font-semibold text-rose-700 block mb-1">Komplettes Skript:</span>
                            <p className="text-slate-700 whitespace-pre-wrap">{selectedPost.ugc_script.full_script}</p>
                          </div>
                        )}
                        <div className="flex gap-2 flex-wrap mt-2 pt-2 border-t border-rose-200">
                          {selectedPost.ugc_script.avatar_style && (
                            <Badge variant="outline" className="text-[10px]">Avatar: {selectedPost.ugc_script.avatar_style}</Badge>
                          )}
                          {selectedPost.ugc_script.background && (
                            <Badge variant="outline" className="text-[10px]">BG: {selectedPost.ugc_script.background}</Badge>
                          )}
                          {selectedPost.ugc_script.estimated_duration && (
                            <Badge variant="outline" className="text-[10px]">Dauer: {selectedPost.ugc_script.estimated_duration}</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Uploaded Image Preview */}
                  {selectedPost.image_url && (
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Hochgeladenes Bild</Label>
                      <img src={selectedPost.image_url} alt="" className="w-full rounded-lg border" />
                    </div>
                  )}

                  {/* Image Upload */}
                  <div>
                    <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" />
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadingImage} data-testid="upload-image-btn">
                        {uploadingImage ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                        Bild hochladen
                      </Button>
                      {canvaStatus.connected && (
                        <Button variant="outline" size="sm" onClick={handleCreateCanvaDesign} disabled={creatingCanvaDesign}>
                          {creatingCanvaDesign ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-1" />}
                          In Canva öffnen
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right: Edit Form */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">Inhalt bearbeiten</Label>
                    <Button variant="ghost" size="sm" onClick={handleRegenerateContent} disabled={regeneratingContent} data-testid="regenerate-content-btn">
                      {regeneratingContent ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                      Neu generieren
                    </Button>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs">Status</Label>
                      <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                        <SelectTrigger className="h-8" data-testid="status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Entwurf</SelectItem>
                          <SelectItem value="approved">Genehmigt</SelectItem>
                          <SelectItem value="published">Veröffentlicht</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs">Titel</Label>
                      <Input
                        value={editForm.title}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        className="h-8"
                        data-testid="edit-title-input"
                      />
                    </div>

                    <div>
                      <Label className="text-xs">Caption</Label>
                      <Textarea
                        value={editForm.caption}
                        onChange={(e) => setEditForm({ ...editForm, caption: e.target.value })}
                        rows={2}
                        data-testid="edit-caption-input"
                      />
                    </div>

                    <div>
                      <Label className="text-xs">Beschreibung</Label>
                      <Textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        rows={3}
                        data-testid="edit-description-input"
                      />
                    </div>

                    <div>
                      <Label className="text-xs">Hashtags</Label>
                      <Input
                        value={editForm.hashtags}
                        onChange={(e) => setEditForm({ ...editForm, hashtags: e.target.value })}
                        placeholder="fitness, ems, training"
                        className="h-8"
                        data-testid="edit-hashtags-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">{editForm.hashtags?.split(",").filter(h => h.trim()).length || 0} Hashtags</p>
                    </div>

                    {/* Social Preview */}
                    <div className="border rounded-lg overflow-hidden bg-white shadow-sm mt-4">
                      <div className="flex items-center gap-2 p-2 border-b bg-slate-50">
                        <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full" />
                        <div>
                          <p className="text-xs font-semibold">Ihr Studio</p>
                        </div>
                        <Badge variant="outline" className="ml-auto text-[10px]">
                          <Instagram className="w-3 h-3 mr-1" />Vorschau
                        </Badge>
                      </div>
                      <div className="p-2">
                        <p className="text-xs">
                          <span className="font-semibold">ihr_studio</span>{" "}
                          <span className="whitespace-pre-wrap">{editForm.caption}</span>
                        </p>
                        <p className="text-xs text-blue-500 mt-1">
                          {editForm.hashtags?.split(",").map(h => `#${h.trim()}`).filter(h => h !== "#").join(" ")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => {
                const text = `${editForm.caption}\n\n${editForm.hashtags?.split(",").map(h => `#${h.trim()}`).filter(h => h !== "#").join(" ")}`;
                copyToClipboard(text);
              }} data-testid="copy-all-btn">
                <Copy className="w-4 h-4 mr-2" />Alles kopieren
              </Button>
              <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>Abbrechen</Button>
              <Button onClick={handleSavePost} data-testid="save-post-btn">
                <Check className="w-4 h-4 mr-2" />Speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
